import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Search, Plus, Trash2, ExternalLink, Loader2, Download, Puzzle, KeyRound, RefreshCw, Users } from "lucide-react";
import { serverStore } from "../../stores/server.store";
import { Card, CardHeader, CardTitle, CardContent } from "../design/Card";
import { Button } from "../design/Button";
import { cn } from "../../utils/cn";
import toast from "react-hot-toast";

interface ModResult {
  source: string;
  id: string;
  name: string;
  author?: string;
  description?: string;
  image?: string | null;
  downloads?: number;
  url?: string;
  installable?: boolean;
  downloadUrl?: string | null;
  version?: string | null;
}

const fmtCount = (n?: number) => {
  if (!n) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
};

/**
 * Multi-source mod browser for Steam servers: Steam Workshop (add by ID/link with
 * auto preview), Thunderstore (real search + one-click install) and Nexus Mods
 * (browse/search, links out — optional API key). Sources come per game from the
 * backend (/steam/mods/sources).
 */
export const SteamModBrowser = observer(({ serverId, meta, onReload }: { serverId: string; meta: any; onReload: () => void }) => {
  const [sources, setSources] = useState<any[]>([]);
  const [sourcesLoaded, setSourcesLoaded] = useState(false);
  const [active, setActive] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState<ModResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [needsKey, setNeedsKey] = useState(false);

  // Workshop state (ordered id list = load order)
  const [mods, setMods] = useState<string[]>(meta?.mods || []);
  const [wsDetails, setWsDetails] = useState<Record<string, ModResult>>({});
  const [wsInput, setWsInput] = useState("");
  const [saving, setSaving] = useState(false);

  const install = serverStore.activeInstalls.get(serverId);
  const busy = !!install && install.status !== "complete" && install.status !== "error";
  const tsInstalled: string[] = meta?.thunderstoreMods || [];

  useEffect(() => { setMods(meta?.mods || []); }, [meta?.mods?.join(",")]);

  // Load available sources for this game
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await serverStore.fetchModSources(serverId);
      if (cancelled) return;
      const list = data.sources || [];
      setSources(list);
      setSourcesLoaded(true);
      if (list.length && !active) setActive(list[0].id);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId]);

  // Enrich workshop ids we don't have details for yet
  useEffect(() => {
    const missing = mods.filter((id) => !wsDetails[id]);
    if (!missing.length) return;
    let cancelled = false;
    serverStore.searchMods({ source: "steamworkshop", ids: missing.join(",") }).then((d: any) => {
      if (cancelled || !d.results) return;
      setWsDetails((prev) => {
        const next = { ...prev };
        d.results.forEach((r: ModResult) => { next[r.id] = r; });
        return next;
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mods.join(",")]);

  // Search (thunderstore/nexus) when the tab activates or on demand
  const runSearch = async (query = q) => {
    if (active === "steamworkshop") return;
    setSearching(true);
    const d: any = await serverStore.searchMods({ source: active, game: meta?.slug, q: query });
    setResults(d.results || []);
    setNeedsKey(!!d.needsKey);
    setSearching(false);
  };
  useEffect(() => {
    if (active && active !== "steamworkshop") { setQ(""); runSearch(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const addWorkshopId = async (id: string, detail?: ModResult) => {
    if (mods.includes(id)) { toast("Mod ist schon in der Liste"); return; }
    setMods((prev: string[]) => [...prev, id]);
    if (detail) { setWsDetails((prev) => ({ ...prev, [id]: detail })); return; }
    const d: any = await serverStore.searchMods({ source: "steamworkshop", ids: id });
    if (d.results?.[0]) setWsDetails((prev) => ({ ...prev, [id]: d.results[0] }));
  };

  const addWorkshop = async () => {
    const id = wsInput.match(/(\d{5,})/)?.[1];
    if (!id) { toast.error("Workshop-ID oder -Link eingeben"); return; }
    setWsInput("");
    await addWorkshopId(id);
  };

  // Workshop-Volltextsuche (nur wenn STEAM_WEB_API_KEY im Backend gesetzt ist)
  const wsSearchable = !!sources.find((s) => s.id === "steamworkshop")?.searchable;
  const [wsQ, setWsQ] = useState("");
  const [wsResults, setWsResults] = useState<ModResult[]>([]);
  const [wsSearching, setWsSearching] = useState(false);
  const runWorkshopSearch = async () => {
    if (!wsQ.trim()) { setWsResults([]); return; }
    setWsSearching(true);
    const d: any = await serverStore.searchMods({ source: "steamworkshop", game: meta?.slug, q: wsQ });
    setWsResults(d.results || []);
    setWsSearching(false);
  };

  const saveWorkshop = async () => {
    setSaving(true);
    const res: any = await serverStore.saveSteamMods(serverId, mods);
    setSaving(false);
    if (res.success) {
      toast.success(res.downloading ? "Mods gespeichert — Download läuft…" : "Mods gespeichert");
      onReload();
    }
  };

  const installTs = async (r: ModResult) => {
    if (!r.downloadUrl) return;
    const res: any = await serverStore.installThunderstoreMod(serverId, { downloadUrl: r.downloadUrl, fullName: r.id });
    if (res.started || res.success) toast.success(`${r.name} wird installiert…`);
    else toast.error(res.message || "Installation fehlgeschlagen");
  };

  if (!sourcesLoaded) {
    return (
      <Card className="bg-ink border-line-strong text-white" noPadding>
        <CardContent className="py-6 flex items-center gap-2 text-sm text-fg-muted"><Loader2 size={16} className="animate-spin" /> Mod-Quellen werden geladen…</CardContent>
      </Card>
    );
  }

  if (!sources.length) {
    return (
      <Card className="bg-ink border-line-strong text-white" noPadding>
        <CardContent className="py-6 text-sm text-fg-muted">Dieses Spiel unterstützt keine Mods über GameManager.</CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-ink border-line-strong text-white" noPadding>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-lg flex items-center"><Puzzle size={18} className="mr-2 text-ember" /> Mods</CardTitle>
        <div className="flex bg-surface-2 rounded-lg p-1 border border-line">
          {sources.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-bold transition-all",
                active === s.id ? "bg-ember/15 text-ember border border-ember/40" : "text-fg-muted hover:text-fg border border-transparent"
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Live install/download progress */}
        {busy && (
          <div>
            <div className="flex justify-between text-xs text-fg-muted mb-1">
              <span>{install?.message || "Läuft…"}</span>
              <span className="font-mono">{Math.round(install?.progress || 0)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <div className="h-full bg-ember transition-all duration-500" style={{ width: `${install?.progress || 0}%` }} />
            </div>
          </div>
        )}

        {/* ---- Steam Workshop ---- */}
        {active === "steamworkshop" && (
          <div className="space-y-3">
            {wsSearchable ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted" />
                    <input
                      value={wsQ}
                      onChange={(e) => setWsQ(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && runWorkshopSearch()}
                      placeholder="Workshop durchsuchen…"
                      className="w-full bg-surface-2 border border-line rounded-lg pl-9 pr-3 py-2 text-sm text-fg placeholder-fg-muted/60 focus:outline-none focus:border-ember/50"
                    />
                  </div>
                  <Button variant="outline" onClick={runWorkshopSearch} className="border-ember/50 text-ember hover:bg-ember/10" disabled={wsSearching}>
                    {wsSearching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                  </Button>
                </div>
                {wsResults.length > 0 && (
                  <div className="space-y-1.5 max-h-56 overflow-y-auto custom-scrollbar pr-1">
                    {wsResults.map((r) => (
                      <div key={r.id} className="flex items-center gap-3 bg-surface-2/60 border border-line rounded-lg px-3 py-1.5">
                        {r.image ? <img src={r.image} alt="" className="w-8 h-8 rounded object-cover shrink-0" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} /> : <div className="w-8 h-8 rounded bg-white/[0.05] shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-fg truncate">{r.name}</div>
                          <div className="text-[11px] text-fg-muted">{r.downloads ? `${fmtCount(r.downloads)} Abos` : "Workshop"}</div>
                        </div>
                        <button onClick={() => addWorkshopId(r.id, r)} disabled={mods.includes(r.id)} className="text-ember disabled:text-fg-muted/50 hover:scale-110 transition-transform" aria-label="Hinzufügen"><Plus size={17} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-fg-muted">
                Tipp: Mit einem kostenlosen <span className="text-fg">STEAM_WEB_API_KEY</span> in der Backend-.env wird hier eine echte
                Workshop-Suche freigeschaltet (steamcommunity.com/dev/apikey). Ohne Key: Mods per ID/Link hinzufügen.
              </p>
            )}
            <div className="flex gap-2">
              <input
                value={wsInput}
                onChange={(e) => setWsInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addWorkshop()}
                placeholder="Workshop-ID oder Link (z.B. 731604991)"
                className="flex-1 bg-surface-2 border border-line rounded-lg px-3 py-2 text-sm text-fg placeholder-fg-muted/60 focus:outline-none focus:border-ember/50"
              />
              <Button variant="outline" onClick={addWorkshop} className="border-ember/50 text-ember hover:bg-ember/10"><Plus size={16} /></Button>
            </div>

            {mods.length === 0 ? (
              <p className="text-sm text-fg-muted">Noch keine Mods. Füge Workshop-IDs oder Links hinzu — Reihenfolge = Ladereihenfolge.</p>
            ) : (
              <div className="space-y-2">
                {mods.map((id, i) => {
                  const d = wsDetails[id];
                  return (
                    <div key={id} className="flex items-center gap-3 bg-surface-2 border border-line rounded-lg px-3 py-2">
                      {d?.image ? (
                        <img src={d.image} alt="" className="w-9 h-9 rounded object-cover shrink-0" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
                      ) : (
                        <div className="w-9 h-9 rounded bg-white/[0.05] flex items-center justify-center shrink-0"><Puzzle size={14} className="text-fg-muted" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-fg truncate">{d?.name || `Workshop ${id}`}</div>
                        <div className="text-[11px] text-fg-muted font-mono">#{i + 1} · {id}{d?.downloads ? ` · ${fmtCount(d.downloads)} Abos` : ""}</div>
                      </div>
                      <a href={`https://steamcommunity.com/sharedfiles/filedetails/?id=${id}`} target="_blank" rel="noreferrer" className="text-fg-muted hover:text-ember" aria-label="Workshop öffnen"><ExternalLink size={15} /></a>
                      <button onClick={() => setMods(mods.filter((m) => m !== id))} className="text-fg-muted hover:text-red-400" aria-label="Entfernen"><Trash2 size={15} /></button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <p className="text-[11px] text-fg-muted">
                {meta?.slug === "ark-se" ? "ARK lädt die Mods beim Serverstart automatisch (automanagedmods)." : "Mods werden per SteamCMD geladen und beim Start aktiviert."}
              </p>
              <Button onClick={saveWorkshop} disabled={saving || busy}>
                {saving || busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />} Mods übernehmen
              </Button>
            </div>
          </div>
        )}

        {/* ---- Thunderstore / Nexus (search + results) ---- */}
        {active !== "steamworkshop" && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runSearch()}
                  placeholder={active === "thunderstore" ? "Thunderstore durchsuchen…" : "Nexus Mods durchsuchen…"}
                  className="w-full bg-surface-2 border border-line rounded-lg pl-9 pr-3 py-2 text-sm text-fg placeholder-fg-muted/60 focus:outline-none focus:border-ember/50"
                />
              </div>
              <Button variant="outline" onClick={() => runSearch()} className="border-ember/50 text-ember hover:bg-ember/10" disabled={searching}>
                {searching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              </Button>
            </div>

            {active === "nexus" && needsKey ? (
              <div className="rounded-lg bg-white/[0.03] border border-line p-4 text-sm text-fg-muted flex gap-3">
                <KeyRound size={18} className="text-warn shrink-0 mt-0.5" />
                <div>
                  Für die Nexus-Suche fehlt ein API-Key. Kostenlos erstellen unter <span className="text-fg">nexusmods.com → Settings → API Keys</span>,
                  dann als <code className="bg-black/30 px-1 py-0.5 rounded text-[11px]">NEXUS_API_KEY</code> in der Backend-<code className="bg-black/30 px-1 py-0.5 rounded text-[11px]">.env</code> eintragen und das Panel neu starten.
                </div>
              </div>
            ) : searching ? (
              <div className="flex items-center gap-2 text-sm text-fg-muted py-6"><Loader2 size={16} className="animate-spin" /> Suche läuft…</div>
            ) : results.length === 0 ? (
              <p className="text-sm text-fg-muted py-2">Keine Treffer.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[420px] overflow-y-auto pr-1 -mr-1">
                {results.map((r) => {
                  const installed = active === "thunderstore" && tsInstalled.includes(r.id);
                  return (
                    <div key={r.id} className="flex gap-3 bg-surface-2 border border-line rounded-xl p-3">
                      {r.image ? (
                        <img src={r.image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-white/[0.05] flex items-center justify-center shrink-0"><Puzzle size={16} className="text-fg-muted" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-fg truncate">{r.name}</div>
                        <div className="text-[11px] text-fg-muted truncate">
                          {r.author}{r.version ? ` · v${r.version}` : ""} · <Users size={10} className="inline -mt-0.5" /> {fmtCount(r.downloads)}
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                          {r.installable && r.downloadUrl ? (
                            <Button size="sm" variant="outline" className="border-ember/50 text-ember hover:bg-ember/10 h-7 px-2.5 text-xs" onClick={() => installTs(r)} disabled={busy || installed}>
                              <Download size={13} className="mr-1" /> {installed ? "Installiert" : "Installieren"}
                            </Button>
                          ) : null}
                          {r.url && (
                            <a href={r.url} target="_blank" rel="noreferrer" className="text-[11px] text-fg-muted hover:text-ember inline-flex items-center gap-1">
                              Seite <ExternalLink size={11} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {active === "thunderstore" && tsInstalled.length > 0 && (
              <p className="text-[11px] text-fg-muted">Installiert: {tsInstalled.join(", ")}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default SteamModBrowser;
